import { IN_STOCK } from '@jouzen/ecom-components/v3/consts/stockStatus';
import type { StockStatus } from '@jouzen/ecom-components/v3/types/stockStatus';
import type { MessageKey } from 'types/LocaleMessageKey';

import type { Order } from '@/app/[locale]/store/_components/HardwarePDP/_types/order';
import MembershipSelectionEnum from '@/app/enums/MembershipSelectionEnum';
import SizeSelectionEnum from '@/app/enums/SizeSelectionEnum';
import WarrantySelectionEnum from '@/app/enums/WarrantySelectionEnum';
import { writeToSessionStorage } from '@/utils/sessionStorage';

export const SIZING_KIT_SUMMARY_KEY: MessageKey =
  'ring_pdp_2025_size_option_sizing_kit';
export const SIZE_SELECTED_SUMMARY_KEY: MessageKey =
  'ring_pdp_2025_size_summary';
export const ONE_MONTH_MEMBERSHIP_SELECTED_SUMMARY_KEY: MessageKey =
  'ring_pdp_2025_monthly_membership_summary';
export const ONE_YEAR_MEMBERSHIP_SELECTED_SUMMARY_KEY: MessageKey =
  'ring_pdp_2025_membership_annual_bullet';
export const CURRENT_MEMBERSHIP_SELECTED_SUMMARY_KEY: MessageKey =
  'ring_pdp_2025_current_member_summary';
export const ONE_YEAR_WARRANTY_SELECTED_SUMMARY_KEY: MessageKey =
  'ring_pdp_2025_warranty_option_1_year';
export const TWO_YEAR_WARRANTY_SELECTED_SUMMARY_KEY: MessageKey =
  'ring_pdp_2025_2_year_warranty_summary';
export const THREE_YEAR_WARRANTY_SELECTED_SUMMARY_KEY: MessageKey =
  'ring_pdp_2025_3_year_warranty_summary';

export type Action =
  | { type: 'SET_ORDER'; payload: Partial<Order> }
  | {
      type: 'UPDATE_FINISH';
      payload: {
        value: string;
      };
    }
  | { type: 'SELECTING_SIZE' }
  | {
      type: 'UPDATE_SIZE';
      payload: {
        value: string;
        summaryKey: MessageKey;
      };
    }
  | {
      type: 'UPDATE_PRODUCT';
      payload: {
        sku: string;
        stockStatus: StockStatus;
      };
    }
  | {
      type: 'UPDATE_RADIO';
      payload: {
        value: string;
        sku: string;
        summaryKey: MessageKey;
        field: 'membership' | 'warranty';
      };
    };

export interface State {
  isSelectingSize: boolean;
  order: Order;
}

export const INITIAL_STATE: State = {
  isSelectingSize: false,
  order: {
    finish: {
      value: '',
    },
    size: {
      value: SizeSelectionEnum.SizingKit,
      summaryKey: SIZING_KIT_SUMMARY_KEY,
    },
    product: {
      sku: '',
      stockStatus: IN_STOCK,
    },
    membership: {
      value: MembershipSelectionEnum.OneMonth,
      sku: '',
      summaryKey: ONE_MONTH_MEMBERSHIP_SELECTED_SUMMARY_KEY,
    },
    warranty: {
      value: WarrantySelectionEnum.OneYear,
      sku: '',
      summaryKey: null,
    },
    ready: false,
  },
};

export const reducer =
  (sessionStorageKey: string) =>
  (state: State, action: Action): State => {
    const newState = { ...state };

    switch (action.type) {
      case 'SET_ORDER': {
        newState.order = {
          ...state.order,
          ...action.payload,
        };
        break;
      }

      case 'UPDATE_FINISH': {
        newState.order = {
          ...state.order,
          finish: {
            ...state.order.finish,
            value: action.payload.value,
          },
        };
        break;
      }

      case 'SELECTING_SIZE': {
        newState.isSelectingSize = true;
        break;
      }

      case 'UPDATE_SIZE': {
        newState.isSelectingSize = false;
        newState.order = {
          ...state.order,
          size: {
            ...state.order.size,
            value: action.payload.value,
            summaryKey: action.payload.summaryKey,
          },
        };
        break;
      }

      case 'UPDATE_PRODUCT': {
        newState.order = {
          ...state.order,
          product: {
            ...state.order.product,
            sku: action.payload.sku,
            stockStatus: action.payload.stockStatus,
          },
        };
        break;
      }

      case 'UPDATE_RADIO': {
        const { value, sku, summaryKey, field } = action.payload;
        newState.order = {
          ...state.order,
          [field]: {
            ...state.order[field],
            value,
            sku,
            summaryKey,
          },
        };
        break;
      }

      default: {
        return newState;
      }
    }

    writeToSessionStorage(sessionStorageKey, JSON.stringify(newState));
    return newState;
  };
